import React, { useCallback, useEffect, useState } from 'react';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import {
  cameraBillingAPI,
  isInsufficientFundsError,
  type CameraBilling,
  type PublicUser,
} from '../../utils/api';

interface CameraSubscriptionsProps {
  /** Баланс поменялся после продления — обновить шапку страницы. */
  onBalanceChanged: (user: PublicUser) => void;
  /** Не хватило денег — открыть пополнение. */
  onTopUp: () => void;
  /** Список камер загружен или изменился — для «Следующего списания» в шапке. */
  onCamerasChanged?: (cameras: CameraBilling[], price: number) => void;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Дата окончания после продления на месяц (как считает бэкенд — от текущей даты окончания). */
function addMonth(value: string | null): string | null {
  const base = value ? new Date(value) : new Date();
  if (Number.isNaN(base.getTime())) return null;
  const next = new Date(base);
  next.setMonth(next.getMonth() + 1);
  // 31 января + месяц → последний день февраля, без перескока в март
  if (next.getDate() !== base.getDate()) next.setDate(0);
  return next.toISOString();
}

function statusLabel(camera: CameraBilling): { text: string; className: string } {
  if (camera.status === 'deleting') {
    return { text: 'Будет удалена', className: 'camera-subs__badge--deleting' };
  }
  if (camera.status === 'active') {
    return { text: 'Активна', className: '' };
  }
  return { text: 'Не активна', className: 'camera-subs__badge--inactive' };
}

function statusHint(camera: CameraBilling): string {
  if (camera.status === 'deleting') {
    return `Удалится ${formatDate(camera.delete_at)}, когда закончится оплата`;
  }
  if (camera.status === 'active') {
    return camera.auto_renew
      ? `Следующее списание ${formatDate(camera.paid_until)}`
      : `Оплачена до ${formatDate(camera.paid_until)}`;
  }
  return camera.paid_until ? `Оплата закончилась ${formatDate(camera.paid_until)}` : 'Не оплачена';
}

/**
 * Оплата камер на странице баланса: каждая камера — отдельная подписка
 * с ценой в месяц, статусом, автопродлением и ручным продлением.
 */
const CameraSubscriptions: React.FC<CameraSubscriptionsProps> = ({
  onBalanceChanged,
  onTopUp,
  onCamerasChanged,
}) => {
  const [loading, setLoading] = useState(true);
  const [cameras, setCameras] = useState<CameraBilling[]>([]);
  const [price, setPrice] = useState(1000);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lowBalance, setLowBalance] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await cameraBillingAPI.list();
      if (res.success) {
        setCameras(res.cameras);
        setPrice(res.price);
      }
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить камеры');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!loading && onCamerasChanged) onCamerasChanged(cameras, price);
  }, [cameras, price, loading, onCamerasChanged]);

  const replace = (next: CameraBilling) =>
    setCameras((prev) => prev.map((c) => (c.id === next.id ? next : c)));

  const run = async (id: number, action: () => Promise<void>) => {
    setError(null);
    setMessage(null);
    setLowBalance(false);
    try {
      setBusyId(id);
      await action();
    } catch (e: any) {
      if (isInsufficientFundsError(e)) {
        setLowBalance(true);
        setError(e?.response?.message || 'Недостаточно средств на балансе');
      } else {
        setError(e?.response?.message || e?.message || 'Не удалось выполнить действие');
      }
    } finally {
      setBusyId(null);
    }
  };

  const toggleAutoRenew = async (camera: CameraBilling) => {
    const enabling = !camera.auto_renew;

    // Неоплаченная камера оплачивается сразу при включении автопродления — предупреждаем о списании
    if (enabling && camera.status === 'inactive') {
      const ok = await confirm({
        title: 'Включить автопродление',
        highlight: (
          <>
            <span>Спишется с баланса сейчас</span>
            <strong>{price.toLocaleString('ru-RU')} ₽</strong>
          </>
        ),
        message: (
          <>
            Камера «{camera.name}» не оплачена, поэтому месяц оплатится сразу, а дальше — автоматически
            каждый месяц. Если на балансе не хватит денег, камера оплатится после пополнения.
          </>
        ),
        confirmText: 'Включить и оплатить',
      });
      if (!ok) return;
    }

    return run(camera.id, async () => {
      const res = await cameraBillingAPI.setAutoRenew(camera.id, enabling);
      replace(res.camera);
      setMessage(res.message);
      if (res.user) onBalanceChanged(res.user);
    });
  };

  const renew = async (camera: CameraBilling) => {
    const isActive = camera.status === 'active';
    const ok = await confirm({
      title: isActive ? 'Продление камеры' : 'Оплата камеры',
      highlight: (
        <>
          <span>Спишется с баланса</span>
          <strong>{price.toLocaleString('ru-RU')} ₽</strong>
        </>
      ),
      message: isActive ? (
        <>
          Камера «{camera.name}» продлится на месяц — до{' '}
          {formatDate(addMonth(camera.paid_until))}.
        </>
      ) : (
        <>Камера «{camera.name}» будет оплачена на месяц, видео и аналитика включатся сразу после оплаты.</>
      ),
      confirmText: isActive ? 'Продлить' : 'Оплатить',
    });
    if (!ok) return;
    return run(camera.id, async () => {
      const res = await cameraBillingAPI.renew(camera.id);
      replace(res.camera);
      setMessage(res.message);
      if (res.user) onBalanceChanged(res.user);
    });
  };

  const cancelDelete = (camera: CameraBilling) =>
    run(camera.id, async () => {
      const res = await cameraBillingAPI.cancelDelete(camera.id);
      replace(res.camera);
      setMessage(res.message);
    });

  const activeCount = cameras.filter((c) => c.status === 'active').length;

  return (
    <section className="balance-card balance-card--tariff">
      {confirmDialog}
      <h2 className="balance-card__title">Камеры</h2>
      <p className="camera-subs__summary">
        {price.toLocaleString('ru-RU')} ₽ в месяц за камеру
        {cameras.length > 0 && ` · оплачено ${activeCount} из ${cameras.length}`}
      </p>

      {loading ? (
        <div className="camera-subs__empty">Загрузка…</div>
      ) : cameras.length === 0 ? (
        <div className="camera-subs__empty">
          Камер пока нет. Добавьте камеру на участке — оплата за первый месяц спишется с баланса.
        </div>
      ) : (
        <ul className="camera-subs__list">
          {cameras.map((camera) => {
            const badge = statusLabel(camera);
            const busy = busyId === camera.id;
            return (
              <li key={camera.id} className="balance-tariff camera-subs__item">
                <div className="balance-tariff__head">
                  <div className="camera-subs__title">
                    <p className="balance-tariff__name camera-subs__name">{camera.name}</p>
                    <p className="balance-tariff__price">
                      {camera.area_name ? `${camera.area_name} · ` : ''}
                      {camera.price.toLocaleString('ru-RU')} ₽ / месяц
                    </p>
                  </div>
                  <span className={`balance-tariff__badge ${badge.className}`}>{badge.text}</span>
                </div>

                <p className="balance-tariff__next">{statusHint(camera)}</p>

                <div className="camera-subs__actions">
                  {camera.status === 'deleting' ? (
                    <button
                      type="button"
                      className="ui-btn ui-btn--outline ui-btn--sm"
                      onClick={() => void cancelDelete(camera)}
                      disabled={busy}
                    >
                      Отменить удаление
                    </button>
                  ) : (
                    <>
                      <label className="camera-subs__toggle">
                        <input
                          type="checkbox"
                          checked={camera.auto_renew}
                          onChange={() => void toggleAutoRenew(camera)}
                          disabled={busy}
                        />
                        <span>Автопродление с баланса</span>
                      </label>
                      <button
                        type="button"
                        className="ui-btn ui-btn--outline ui-btn--sm"
                        onClick={() => void renew(camera)}
                        disabled={busy}
                      >
                        {camera.status === 'active' ? 'Продлить' : 'Оплатить'}
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div className="camera-subs__message camera-subs__message--error">
          {error}
          {lowBalance && (
            <button type="button" className="camera-subs__link" onClick={onTopUp}>
              Пополнить баланс
            </button>
          )}
        </div>
      )}
      {message && !error && <div className="camera-subs__message">{message}</div>}
    </section>
  );
};

export default CameraSubscriptions;
